import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search } from 'lucide-react';

const HistoryPage = () => {
  const navigate = useNavigate();
  
  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [searchTerm, setSearchTerm] = useState(''); 
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchDailyHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get('/history/daily-qc', {
        params: { 
          start_date: fromDate, 
          end_date: toDate,
          status: 'Approved',
          search: searchTerm 
        }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchDailyHistory();
  }, []); 

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') searchDailyHistory();
  };

  return (
    <div className="max-w-7xl mx-auto p-4" data-testid="history-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Daily QC History</h1>
      </div>

      {/* Filter & Search Section */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-full md:w-auto flex-1 min-w-[150px]">
            <Label className="mb-1 block text-sm">From Date</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-10" />
          </div>
          <div className="w-full md:w-auto flex-1 min-w-[150px]">
            <Label className="mb-1 block text-sm">To Date</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-10" />
          </div>
          <div className="w-full md:w-auto flex-[2] min-w-[200px]">
            <Label className="mb-1 block text-sm">Search (Serial / Asset ID / Customer)</Label>
            <Input
              type="text"
              placeholder="Serial, Asset ID or Customer Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyPress}
              className="h-10"
            />
          </div>
          <Button onClick={searchDailyHistory} disabled={loading} className="h-10 px-6 font-bold uppercase bg-[#0056D2] hover:bg-[#0046a8] text-white">
            <Search className="w-4 h-4 mr-2" />
            {loading ? '...' : 'Filter'}
          </Button>
        </div>
      </div>

      {/* Data Table Section - Fixed Width */}
      {!loading && (
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden w-full">
          <div className="p-4 border-b">
            <h2 className="text-lg font-bold">{history.length} record(s) found</h2>
          </div>
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs md:text-sm">
              <thead>
                <tr className="bg-[#4bc5c5] text-black">
                  <th className="p-3 border-b font-bold">Customer</th>
                  <th className="p-3 border-b font-bold">Product</th>
                  <th className="p-3 border-b font-bold">Model</th>
                  <th className="p-3 border-b font-bold">Serial / Kol No</th>
                  <th className="p-3 border-b font-bold">RAM</th>
                  <th className="p-3 border-b font-bold">SSD</th>
                  <th className="p-3 border-b font-bold">Order Type</th>
                  <th className="p-3 border-b font-bold">QC Event Type</th>
                  <th className="p-3 border-b font-bold">Date</th>
                  <th className="p-3 border-b font-bold">1st QC</th>
                  <th className="p-3 border-b font-bold">2nd QC Engineer</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={item.id || index} className="border-b hover:bg-gray-50">
                    <td className="p-3">{item.customer_or_company_name || '-'}</td>
                    <td className="p-3">{item.product_category || '-'}</td>
                    <td className="p-3">{item.model_number || '-'}</td>
                    <td className="p-3 font-semibold">{item.serial_number || item.asset_id || '-'}</td>
                    <td className="p-3">{item.ram_size || '-'}</td>
                    <td className="p-3">{item.ssd_size || '-'}</td>
                    <td className="p-3">{item.order_type || '-'}</td>
                    <td className="p-3">{item.qc_event_type || '-'}</td>
                    <td className="p-3">{item.updated_timestamp ? new Date(item.updated_timestamp).toLocaleDateString("en-GB") : "-"}</td>
                    <td className="p-3">{item.created_by_technician || '-'}</td>
                    <td className="p-3">{item.second_qc_engineer || item.engineer_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;